// Infinite Scroll for Collection Pages
class InfiniteScroll {
  constructor() {
    this.collectionGrid = document.getElementById('collection-grid');
    this.loadingIndicator = document.getElementById('loading-indicator');
    this.endMessage = document.getElementById('end-message');
    this.collectionDataScript = document.getElementById('collection-data');
    
    if (!this.collectionGrid) {
      return; // Not on a collection page
    }
    
    // Check if infinite scroll is enabled
    const paginationType = this.collectionGrid.getAttribute('data-pagination-type');
    if (paginationType !== 'infinite' || !this.collectionDataScript) {
      return; // Not using infinite scroll
    }
    
    this.collectionData = JSON.parse(this.collectionDataScript.textContent);
    this.isLoading = false;
    this.hasMorePages = this.collectionData.currentPage < this.collectionData.totalPages;
    
    this.init();
  }
  
  init() {
    // Set up intersection observer for scroll detection
    this.setupScrollObserver();
    
    // Show end message if no more pages
    if (!this.hasMorePages) {
      this.showEndMessage();
    }
  }
  
  setupScrollObserver() {
    // Validate collection grid exists and has a parent
    if (!this.collectionGrid || !this.collectionGrid.parentNode) {
      console.warn('InfiniteScroll: Collection grid or parent not found');
      return;
    }
    
    // Create a sentinel element at the bottom of the grid
    this.sentinel = document.createElement('div');
    this.sentinel.className = 'scroll-sentinel';
    this.sentinel.style.height = '1px';
    this.sentinel.style.position = 'absolute';
    this.sentinel.style.bottom = '200px'; // Trigger 200px before reaching bottom
    this.sentinel.style.width = '100%';
    
    try {
      // Insert sentinel after the collection grid
      this.collectionGrid.parentNode.insertBefore(this.sentinel, this.collectionGrid.nextSibling);
    } catch (error) {
      console.error('InfiniteScroll: Failed to insert sentinel element:', error);
      return;
    }
    
    // Validate sentinel was created and is a valid Node
    if (!this.sentinel || !(this.sentinel instanceof Node)) {
      console.error('InfiniteScroll: Sentinel is not a valid Node');
      return;
    }
    
    // Set up intersection observer
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMorePages && !this.isLoading) {
          this.loadMoreProducts();
        }
      });
    }, {
      rootMargin: '100px' // Start loading 100px before sentinel comes into view
    });
    
    try {
      this.observer.observe(this.sentinel);
    } catch (error) {
      console.error('InfiniteScroll: Failed to observe sentinel:', error);
      // Fallback to scroll event listener
      this.setupScrollFallback();
    }
  }
  
  async loadMoreProducts() {
    if (this.isLoading || !this.hasMorePages) return;
    
    this.isLoading = true;
    this.showLoadingIndicator();
    
    try {
      const nextPage = this.collectionData.currentPage + 1;
      const url = this.buildNextPageUrl(nextPage);
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch products');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract new products - check for both custom and standard collection cards
      const newProducts = doc.querySelectorAll('.custom-collection-card, .grid__item');
      const newCollectionData = doc.getElementById('collection-data');
      
      if (newProducts.length > 0) {
        // Add new products to the grid with animation
        this.addProductsToGrid(newProducts);
        
        // Update collection data
        if (newCollectionData) {
          this.collectionData = JSON.parse(newCollectionData.textContent);
          this.hasMorePages = this.collectionData.currentPage < this.collectionData.totalPages;
        }
      } else {
        this.hasMorePages = false;
      }
      
      // Check if we've reached the end
      if (!this.hasMorePages) {
        this.showEndMessage();
        this.observer.disconnect(); // Stop observing
      }
      
    } catch (error) {
      console.error('Error loading more products:', error);
      // Hide loading indicator on error
      this.hideLoadingIndicator();
    } finally {
      this.isLoading = false;
    }
  }
  
  buildNextPageUrl(page) {
    const currentUrl = new URL(window.location);
    currentUrl.searchParams.set('page', page);
    return currentUrl.toString();
  }
  
  addProductsToGrid(newProducts) {
    // Create a document fragment for better performance
    const fragment = document.createDocumentFragment();
    
    newProducts.forEach((product, index) => {
      // Clone the product element
      const productClone = product.cloneNode(true);
      
      // Add fade-in animation
      productClone.style.opacity = '0';
      productClone.style.transform = 'translateY(20px)';
      productClone.style.transition = 'opacity 0.5s ease, transform 0.5s ease';
      
      fragment.appendChild(productClone);
      
      // Trigger animation after a small delay
      setTimeout(() => {
        productClone.style.opacity = '1';
        productClone.style.transform = 'translateY(0)';
      }, index * 100); // Stagger animations
    });
    
    // Add all products to the grid at once
    this.collectionGrid.appendChild(fragment);
    
    // Hide loading indicator
    this.hideLoadingIndicator();
  }
  
  showLoadingIndicator() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'block';
    }
  }
  
  hideLoadingIndicator() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
  }
  
  showEndMessage() {
    this.hideLoadingIndicator();
    if (this.endMessage) {
      this.endMessage.style.display = 'block';
    }
  }
  
  setupScrollFallback() {
    console.log('InfiniteScroll: Using scroll fallback method');
    
    // Fallback scroll event listener
    const handleScroll = () => {
      if (this.isLoading || !this.hasMorePages) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Trigger when user is 200px from bottom
      if (scrollTop + windowHeight >= documentHeight - 200) {
        this.loadMoreProducts();
      }
    };
    
    // Throttle scroll events for better performance
    let scrollTimeout;
    const throttledScroll = () => {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(() => {
        handleScroll();
        scrollTimeout = null;
      }, 100);
    };
    
    window.addEventListener('scroll', throttledScroll);
    
    // Store reference for cleanup
    this.scrollHandler = throttledScroll;
  }
  
  cleanup() {
    // Clean up observers and event listeners
    if (this.observer) {
      this.observer.disconnect();
    }
    
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    
    if (this.sentinel && this.sentinel.parentNode) {
      this.sentinel.parentNode.removeChild(this.sentinel);
    }
  }
}

// Initialize infinite scroll when DOM is loaded
document.addEventListener('DOMContentLoaded', function() {
  new InfiniteScroll();
});

// Reinitialize on page navigation (for SPA-like behavior)
document.addEventListener('shopify:section:load', function() {
  new InfiniteScroll();
});
